/**
 * The two mutations Journal home performs itself: favourite and delete
 * (F-JR-15, F-JR-16).
 *
 * Both live here rather than inside a component because both are transactions
 * with rules that have nothing to do with rendering, and a rule expressed as a
 * click handler can only be checked by rendering one.
 *
 * **Favourite writes through whichever store owns the newest truth.** When a
 * same-id draft exists it is the draft that is rewritten, with its **revision
 * left untouched** — the draft has already reserved `entry.revision + 1`, and
 * bumping it here would make the eventual promotion a two-step increment that
 * the repository's exact `current + 1` rule rejects. `savedAt` still advances
 * by at least one microsecond so draft ordering stays strictly increasing, and
 * `updatedAt` advances so the timeline reorders. Only when there is no draft is
 * the durable entry saved, and then with exactly one increment.
 *
 * **Delete is one transaction over the entry, its same-id draft and its whole
 * attachment set**, which is the repository's own `deleteEntry` contract — so
 * nothing here decomposes it into steps that could half-succeed.
 */

import type {
  JournalDraft,
  JournalEntry,
  JournalEntryProjection,
  JournalInstant,
  JournalRepositoryErrorCode,
} from "@ff/domain";
import {
  journalInstantFromMilliseconds,
  nextJournalRevision,
  nextJournalSavedAt,
} from "@ff/domain";
import type { JournalRepositoryPort } from "@ff/domain/ports";

export type JournalMutationResult =
  | { readonly kind: "ok" }
  | { readonly kind: "failed"; readonly error?: JournalRepositoryErrorCode };

/**
 * The favourite flag on the record that owns the newest truth.
 *
 * `nowMilliseconds` is passed in rather than read from a clock here, because
 * this module is reachable from a test that pins time and from a surface that
 * holds the clock port — and a second time source is a second answer to "which
 * save is newer".
 */
export async function toggleJournalFavorite(
  repository: JournalRepositoryPort,
  projection: JournalEntryProjection,
  nowMilliseconds: number,
): Promise<JournalMutationResult> {
  const now = journalInstantFromMilliseconds(nowMilliseconds);

  if (projection.isDraft) {
    return saveFavoriteDraft(repository, projection.draft, now);
  }
  return saveFavoriteEntry(repository, projection.entry, now);
}

async function saveFavoriteDraft(
  repository: JournalRepositoryPort,
  draft: JournalDraft,
  now: JournalInstant,
): Promise<JournalMutationResult> {
  const entry: JournalEntry = {
    ...draft.entry,
    isFavorite: !draft.entry.isFavorite,
    // Never earlier than what it already claims: a clock that moved backwards
    // must not make an edit look older than the edit it replaces.
    updatedAt: Math.max(now, draft.entry.updatedAt) as JournalInstant,
  };
  const next: JournalDraft = {
    ...draft,
    entry,
    savedAt: nextJournalSavedAt(now, draft.savedAt),
  };
  const result = await repository.saveDraft(next);
  return result.kind === "written" ? { kind: "ok" } : { kind: "failed", error: result.error };
}

async function saveFavoriteEntry(
  repository: JournalRepositoryPort,
  entry: JournalEntry,
  now: JournalInstant,
): Promise<JournalMutationResult> {
  const next: JournalEntry = {
    ...entry,
    // `nextJournalRevision` saturates; it is used rather than `+ 1` for exactly
    // that reason. A wrapped revision would compare as *older* than the record
    // it replaces, and the repository would then refuse every later write to
    // that entry for the life of the file.
    revision: nextJournalRevision(entry.revision),
    isFavorite: !entry.isFavorite,
    updatedAt: Math.max(now, entry.updatedAt) as JournalInstant,
  };
  const result = await repository.saveEntry(next);
  return result.kind === "written" ? { kind: "ok" } : { kind: "failed", error: result.error };
}

/**
 * F-JR-16. `nothingToDelete` is reported as success, not failure: the entry is
 * not there, which is what the user asked for, and telling them otherwise
 * invites a second delete of something already gone.
 */
export async function deleteJournalEntry(
  repository: JournalRepositoryPort,
  entry: JournalEntry,
): Promise<JournalMutationResult> {
  const result = await repository.deleteEntry(entry.id);
  if (result.kind === "failed") return { kind: "failed", error: result.error };
  return { kind: "ok" };
}
